import ThreeDotMenu, {
  type ThreeDotMenuItem,
} from "@/Shared/Components/ThreeDotMenu";
import { QuestionBubbleLabel } from "@/Shared/Components/QuestionBubble";

import { CheckIcon } from "@phosphor-icons/react/Check";
import { ClockIcon } from "@phosphor-icons/react/Clock";
import { BankLogo } from "@/Shared/Icons/BankLogo";
import type { CreditCard } from "@freightsimple/generated-dashboard-openapi-client/models/CreditCard";
import type { BankAccount } from "@freightsimple/generated-dashboard-openapi-client/models/BankAccount";
import { BankAccountVerificationStatus } from "@freightsimple/generated-dashboard-openapi-client/models/BankAccountVerificationStatus";
import { Badge } from "@freightsimple/ui-core/Display/Badge";
import { Banner } from "@freightsimple/ui-core/Display/Banner";
import { Button } from "@freightsimple/ui-core/Controls/Button";
import { Table, type Column } from "@freightsimple/ui-core/Table/Table";
import { Tooltip } from "@freightsimple/ui-core/Feedback/Tooltip";

interface BankAccountsSectionProps {
  bankAccounts: Array<BankAccount>;
  creditCards: Array<CreditCard>;
  setDefaultBankAccount: (stripePaymentMethodId: string) => void;
  removeBankAccount: (stripePaymentMethodId: string) => void;
  allowRemoveAccountButton: boolean;
  verifyBankAccount: (bankAccount: BankAccount) => void;
}

// The backend sends microdepositArrivalDate as a UTC calendar date (e.g.
// "2026-05-28") which the generated client parses to a Date at UTC midnight.
// Format in UTC so the browser's local timezone doesn't shift it back a day.
function formatArrivalDate(date: Date): string {
  return new Intl.DateTimeFormat("en-US", {
    timeZone: "UTC",
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(date);
}

function BankAccountsSection(props: BankAccountsSectionProps) {
  const {
    bankAccounts,
    creditCards,
    setDefaultBankAccount,
    removeBankAccount,
    allowRemoveAccountButton,
    verifyBankAccount,
  } = props;

  function itemsForPaymentMethod(bankAccount: BankAccount) {
    const { stripePaymentMethodId } = bankAccount;
    const isPending =
      bankAccount.verificationStatus ===
      BankAccountVerificationStatus.PendingVerification;
    const items: Array<ThreeDotMenuItem> = [];

    items.push({
      name: "Make Default",
      action: function () {
        setDefaultBankAccount(stripePaymentMethodId);
      },
      disabled: bankAccount.isDefault || isPending,
      disabledExplanation: isPending
        ? "Verify the bank account first"
        : "This is already default",
    });

    // Pending accounts can always be removed — they're not chargeable yet, so
    // allowRemoveAccountButton (which guards the last usable method) doesn't
    // apply to them.
    items.push({
      name: "Remove Bank Account",
      action: function () {
        removeBankAccount(stripePaymentMethodId);
      },
      disabled: !isPending && !allowRemoveAccountButton,
      disabledExplanation: "Cannot remove last payment method",
    });

    return items;
  }

  const bankAccountColumns: Column<BankAccount>[] = [
    {
      key: "bank",
      header: "Saved Bank Accounts",
      render: (ba) => (
        <BankLogo
          institutionNumber={ba.institutionNumber}
          stripeBankName={ba.bankName}
        />
      ),
    },
    {
      key: "account",
      header: "",
      width: "260px",
      render: (ba) => {
        const isPending =
          ba.verificationStatus ===
          BankAccountVerificationStatus.PendingVerification;
        return (
          <div>
            <div className="text-base font-light text-content-muted">
              Account Ending {ba.lastFourDigits}
            </div>
            {isPending && (
              <div className="mt-1 text-base text-content-muted">
                {ba.microdepositArrivalDate
                  ? `Micro-deposits arrive by ${formatArrivalDate(
                      ba.microdepositArrivalDate,
                    )}.`
                  : "Stripe is sending two micro-deposits."}{" "}
                {/* `Button`, not `Link`: inside a Table the Button ignores
                    `size="link"` and renders as a real bordered action, which is
                    what a dense row wants. It is not prose-link text. */}
                <Button size="link" onClick={() => verifyBankAccount(ba)}>
                  Enter amounts to verify
                </Button>
              </div>
            )}
          </div>
        );
      },
    },
    {
      key: "status",
      header: "",
      width: "160px",
      render: (ba) => {
        const isPending =
          ba.verificationStatus ===
          BankAccountVerificationStatus.PendingVerification;
        if (isPending) {
          return (
            <Badge
              label="Pending verification"
              icon={ClockIcon}
              variant="warning"
            />
          );
        }
        if (ba.isDefault) {
          return (
            <Tooltip
              side="right"
              content={
                <div className="p-2.5">
                  <QuestionBubbleLabel>
                    This is the default payment method that will be used for
                    shipments.
                  </QuestionBubbleLabel>
                </div>
              }
            >
              <Badge label="Default" icon={CheckIcon} variant="success" />
            </Tooltip>
          );
        }
        return undefined;
      },
    },
  ];

  const defaultCreditCard = creditCards.some(function (cc) {
    return cc.isDefault;
  });
  const defaultBankAccount = bankAccounts.some(function (ba) {
    return ba.isDefault;
  });
  const showBanner = defaultCreditCard && !defaultBankAccount;

  return (
    <>
      {showBanner && (
        <>
          <div className="h-[16px]" />
          <Banner variant="warning">
            Your current default payment method is set to a credit card
          </Banner>
        </>
      )}
      <div className="h-[16px]" />
      <Table
        tableId="bank-accounts"
        columns={bankAccountColumns}
        data={bankAccounts}
        rowKey={(ba) => ba.stripePaymentMethodId}
        emptyMessage="No saved bank accounts"
        rowActions={(bankAccount) => (
          <ThreeDotMenu items={itemsForPaymentMethod(bankAccount)} />
        )}
      />
    </>
  );
}
export default BankAccountsSection;
